import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, TextField, IconButton, Button } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import InfoNotice from "./InfoNotice";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  clampCacheDimension,
  applyMinFrontToRearHeight,
} from "../../helpers/evalCacheDimFormula";

// Convention BACKOFFICE des axes (correspond aux cles dimFormulas) :
//   sizeX      = profondeur (mm)
//   sizeY      = largeur (mm)
//   sizeZ      = hauteur AVANT du cache (mm) -- hauteur "maitresse" reference DXF
//   rearHeight = hauteur ARRIERE du cache (mm)
// Ordre de calcul / disponibilite des variables intermediaires :
//   sizeX -> sizeZ -> rearHeight -> sizeY
//
// L'evaluateur lui-meme est mutualise dans helpers/evalCacheDimFormula.js
// (SYNC avec usipanel-backoffice/lib/evalCacheDimFormula.js).

const CacheDimensionsForm = ({
  handleKeyDown,
  sizeX,
  sizeY,
  sizeZ,
  frontHeight,
  onSizeXChange,
  onRearYChange,
  onSizeZChange,
  dimensionsErrorString,
  dimensionsNeedDevis,
  isDimensionsError,
  P1,
  P2,
  H,
  L,
  installationType,
  bottom,
  rear = false,
  // 🆕 WALLMOUNT = état indépendant (toggle fixation murale/au sol).
  // Default true pour rétro-compat avec les appels qui ne fournissent pas
  // encore la prop (tests + appels historiques).
  wallmount = true,
  onDimensionsManualChange,
  onResetCacheDimensions,
  // Vrai dès qu'au moins un axe du cache est verrouillé manuellement (état
  // persistant côté modèle). Source de vérité pour afficher "Annuler mes
  // modifications", y compris après remontage / changement d'onglet.
  hasManualEdits = false,
  dimFormulas,
  // Minimums DURS calculés en amont (ConfiguratorForm) à partir des formules
  // maîtresses `cache_dim_formulas` pour les L/H/P1/P2 courants.
  cacheMinimums = null,
  productDec = null,
}) => {
  const theme = useTheme();
  const [view, setView] = useState("default");
  const [isChanged, setIsChanged] = useState(false);
  const [tempSizeX, setTempSizeX] = useState((sizeX / 10).toFixed(1)); // Valeur temporaire pour sizeX
  const [tempSizeY, setTempSizeY] = useState((sizeY / 10).toFixed(1)); // Valeur temporaire pour sizeY
  const [tempSizeZ, setTempSizeZ] = useState((sizeZ / 10).toFixed(1)); // Valeur temporaire pour sizeZ
  const [previousX, setPreviousX] = useState(sizeX);
  const [previousY, setPreviousY] = useState(sizeY);
  const [previousZ, setPreviousZ] = useState(sizeZ);
  const [tempFrontHeight, setTempFrontHeight] = useState(
    (frontHeight / 10).toFixed(1)
  ); // Valeur temporaire pour le frontHeight
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));

  // Minimums durs. Largeur/profondeur sont des seuils fixes clampés ici au blur.
  // La hauteur ARRIÈRE n'a pas de seuil statique : le minimum porte sur la
  // hauteur AVANT (≥ minFrontHeight) et l'arrière est ajustée en conséquence
  // dans ConfiguratorForm.recalculateDimensions (re-sync via la prop sizeY).
  const minWidth = cacheMinimums?.minWidth ?? 0;
  const minDepth = cacheMinimums?.minDepth ?? 0;
  const minFrontHeight = cacheMinimums?.minFrontHeight ?? 0;

  const handleClick = () => {
    // Change la vue à "default" seulement si elle n'est pas déjà en "default"
    if (view !== "default") {
      setView("default");
    }
  };

  const handleResetClick = () => {
    onDimensionsManualChange?.(false);
    onResetCacheDimensions?.();
    setIsChanged(false);
  };

  const handleBlurSizeX = () => {
    setTempSizeX((prev) => {
      const numericValue = parseFloat(prev.replace(",", "."));
      if (!isNaN(numericValue)) {
        const clampedMm = clampCacheDimension(
          numericValue * 10,
          minWidth
        );
        if (clampedMm !== previousX) {
          setPreviousX(clampedMm);
          setIsChanged(true);
          onSizeXChange(clampedMm);
        }
        return (clampedMm / 10).toFixed(1);
      }
      return (sizeX / 10).toFixed(1);
    });
  };
  const handleBlurSizeY = () => {
    setTempSizeY((prev) => {
      const numericValue = parseFloat(prev.replace(",", "."));
      if (!isNaN(numericValue)) {
        const valueMm = numericValue * 10;
        const { rearY: effectiveMm } = applyMinFrontToRearHeight(
          valueMm,
          sizeZ,
          minFrontHeight
        );
        if (effectiveMm !== previousY) {
          setPreviousY(effectiveMm);
          setIsChanged(true);
          onRearYChange(effectiveMm);
        }
        return (effectiveMm / 10).toFixed(1);
      }
      return (sizeY / 10).toFixed(1);
    });
  };
  const handleBlurSizeZ = () => {
    setTempSizeZ((prev) => {
      const numericValue = parseFloat(prev.replace(",", "."));
      if (!isNaN(numericValue)) {
        const clampedMm = clampCacheDimension(
          numericValue * 10,
          minDepth
        );
        if (clampedMm !== previousZ) {
          setPreviousZ(clampedMm);
          setIsChanged(true);
          onSizeZChange(clampedMm);
        }
        return (clampedMm / 10).toFixed(1);
      }
      return (sizeZ / 10).toFixed(1);
    });
  };


  useEffect(() => {
    setPreviousX(sizeX);
    setPreviousY(sizeY);
    setPreviousZ(sizeZ);
  }, [sizeX, sizeY, sizeZ]);

  // Le bouton "Annuler mes modifications" suit l'état persistant : il reste
  // visible tant qu'un axe est verrouillé, et disparaît quand tous les verrous
  // ont été relâchés (réinitialisation OU modification des dimensions
  // d'installation correspondantes).
  useEffect(() => {
    setIsChanged(!!hasManualEdits);
  }, [hasManualEdits]);

  const handleChangeSizeX = (e) => {
    let value = e.target.value;
    if (value.match(/[^0-9.,]/)) return; // Empêcher les caractères non numériques
    if (value.includes(",")) value = value.replace(",", "."); // Remplacer la virgule par un point
    setTempSizeX(value);
    onDimensionsManualChange?.(true);
  };

  const handleChangeSizeY = (e) => {
    let value = e.target.value;
    if (value.match(/[^0-9.,]/)) return;
    if (value.includes(",")) value = value.replace(",", ".");
    setTempSizeY(value);
    onDimensionsManualChange?.(true);
  };

  const handleChangeSizeZ = (e) => {
    let value = e.target.value;
    if (value.match(/[^0-9.,]/)) return;
    if (value.includes(",")) value = value.replace(",", ".");
    setTempSizeZ(value);
    onDimensionsManualChange?.(true);
  };

  useEffect(() => {
    if (isNaN(frontHeight)) return;
    setTempFrontHeight((frontHeight / 10).toFixed(1));
  }, [frontHeight]);

  useEffect(() => {
    if (isNaN(sizeX)) return;
    setTempSizeX((sizeX / 10).toFixed(1));
  }, [sizeX]);

  useEffect(() => {
    if (isNaN(sizeY)) return;
    setTempSizeY((sizeY / 10).toFixed(1));
  }, [sizeY]);

  useEffect(() => {
    if (isNaN(sizeZ)) return;
    setTempSizeZ((sizeZ / 10).toFixed(1));
  }, [sizeZ]);

  const renderSideView = () => {
    return (
      <>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/types/dimensions-side.png`} // Remplace par l'URL de l'image
          alt="Dimensions du cache"
          sx={{ width: "100%", borderRadius: 2 }}
        />
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: 12, sm: 12, md: 15 },
            fontWeight: "800",
            position: "absolute",
            top: "2%", // Adjust the position as needed
            left: "10px", // Adjust the position as needed
          }}
        >
          Vue de profil
        </Typography>
        {/* Close icon */}
        <IconButton
          sx={{
            position: "absolute",
            top: "10px", // Adjust the position as needed
            right: "10px", // Adjust the position as needed
            bgcolor: "black",
            color: "white",
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CloseIcon sx={{ fontSize: "16px" }} />
        </IconButton>

        {/* angle */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            right: "39%",
            transform: "translate(50%, -50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#2c8998",
            color: "white",
            width: "30px",
            height: "20px",
            borderRadius: "10px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {"2°"}
          </Typography>
        </Box>

        {/* jointure haute variable */}
        <Box
          sx={{
            position: "absolute",
            top: "14%",
            right: "18%",
            transform: "translate(50%, -50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#2c8998",
            color: "white",
            width: "40px",
            height: "20px",
            borderRadius: "10px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {(
              Math.ceil(80 + sizeZ * Math.tan((2 * Math.PI) / 180)) / 10
            ).toFixed(1)}
          </Typography>
        </Box>

        {/* ecart mur */}
        <Box
          sx={{
            position: "absolute",
            top: "48%",
            right: "33%",
            transform: "translate(50%, -50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#2c8998",
            color: "white",
            width: "30px",
            height: "20px",
            borderRadius: "10px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {8.5}
          </Typography>
        </Box>

        {/* hauteur variable */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            right: "18%",
            transform: "translate(50%, -50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#2c8998",
            color: "white",
            width: "40px",
            height: "20px",
            borderRadius: "10px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {(
              (sizeY -
                2 * 80 -
                Math.ceil(sizeZ * Math.tan((2 * Math.PI) / 180))) /
              10
            ).toFixed(1)}
          </Typography>
        </Box>

        {/* jointure bas */}
        <Box
          sx={{
            position: "absolute",
            bottom: "13%",
            right: "18%",
            transform: "translate(50%, -50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#2c8998",
            color: "white",
            width: "30px",
            height: "20px",
            borderRadius: "10px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            8.0
          </Typography>
        </Box>
      </>
    );
  };

  const renderTopView = () => {
    return (
      <>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/types/dimensions-top.png`}
          alt="Dimensions du cache"
          sx={{ width: "100%", borderRadius: 2 }}
        />
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: 12, sm: 12, md: 15 },
            fontWeight: "800",
            position: "absolute",
            top: "2%", // Adjust the position as needed
            left: "10px", // Adjust the position as needed
          }}
        >
          Vue supérieure
        </Typography>

        {/* Close icon */}
        <IconButton
          sx={{
            position: "absolute",
            top: "10px", // Adjust the position as needed
            right: "10px", // Adjust the position as needed
            bgcolor: "black",
            color: "white",
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CloseIcon sx={{ fontSize: "16px" }} />
        </IconButton>

        {/* jointure 1 */}
        <Box
          sx={{
            position: "absolute",
            top: "14%",
            right: "24%",
            transform: "translate(50%, -50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#2c8998",
            color: "white",
            width: "30px",
            height: "20px",
            borderRadius: "10px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            8.0
          </Typography>
        </Box>

        {/* ecart mur */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            right: "37%",
            transform: "translate(50%, -50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#2c8998",
            color: "white",
            width: "30px",
            height: "20px",
            borderRadius: "10px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {8.5}
          </Typography>
        </Box>

        {/* longueur variable */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            right: "24%",
            transform: "translate(50%, -50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#2c8998",
            color: "white",
            width: "40px",
            height: "20px",
            borderRadius: "10px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {((sizeX - 80 * 2) / 10).toFixed(1)}
          </Typography>
        </Box>

        {/* jointure 2 */}
        <Box
          sx={{
            position: "absolute",
            bottom: "9%",
            right: "24%",
            transform: "translate(50%, -50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#2c8998",
            color: "white",
            width: "30px",
            height: "20px",
            borderRadius: "10px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            8.0
          </Typography>
        </Box>
      </>
    );
  };

  const renderZoomView = () => {
    return (
      <>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/types/dimensions-zoom.png`}
          alt="Dimensions du cache"
          sx={{ width: "100%", borderRadius: 2 }}
        />
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: 12, sm: 12, md: 15 },
            fontWeight: "800",
            position: "absolute",
            top: "2%", // Adjust the position as needed
            left: "10px", // Adjust the position as needed
          }}
        >
          Déport goutte d'eau anti-ruissellement
        </Typography>
        {/* Close icon */}
        <IconButton
          sx={{
            position: "absolute",
            top: "10px", // Adjust the position as needed
            right: "10px", // Adjust the position as needed
            bgcolor: "black",
            color: "white",
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CloseIcon sx={{ fontSize: "16px" }} />
        </IconButton>
      </>
    );
  };

  const renderDefaultView = () => {
    return (
      <>
        <Box sx={{ position: "relative", width: "100%" }}>
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/types/default-dimensions.png`}
            alt="Dimensions du cache"
            sx={{ width: "100%", borderRadius: 2 }}
          />

          {/* Largeur (sizeX) */}
          <Box
            sx={{
              position: "absolute",
              bottom: "22%",
              left: "36%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "white",
              height: "35px",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              background: "#fff !important",
              width: "70px !important",
              borderColor: "#3397a6",
              borderRadius: "50px",
              borderWidth: 1, // Ajoutez une largeur de bordure explicite
              borderStyle: "solid", // Définir un style de bordure
              zIndex: selectedInfo === "Largeur" ? 5 : 1,
            }}
          >
            <TextField
              value={tempSizeX} // Utilisation de la valeur temporaire
              onChange={handleChangeSizeX} // Mise à jour de la valeur temporaire
              onBlur={handleBlurSizeX} // Mise à jour finale lors de la perte du focus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleBlurSizeX();
                  e.target.blur();
                }
              }}
              variant="outlined"
              size="small"
              sx={{
                width: "90%",
                textAlign: "center",
                "& input": {
                  textAlign: "center",
                  fontWeight: "900",
                  fontFamily:
                    "'Montserrat', Helvetica, Arial, Lucida, sans-serif",
                  fontSize: "13px",
                },
                "& fieldset": { border: "none" },
              }}
            />
            <InfoIcon
              sx={{
                color: "#acacac",
                fontSize: "15px",
                position: "absolute",
                top: "29%",
                right: "3%",
                ml: 1,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleInfoClick("Largeur");
              }}
            />
            {selectedInfo === "Largeur" && (
              <Box
                ref={popupRef}
                sx={{
                  position: "absolute",
                  top: isXs ? "35%" : "35%",
                  right: isXs ? "-43.5vw" : "12%",
                  mt: 1,
                  width: isXs ? "80vw" : "400px",
                  zIndex: 6,
                  fontWeight: "300",
                  padding: "20px",
                  backgroundColor: "#f9f9f9",
                  boxShadow: "0 0 35px rgb(0 0 0 / 19%)",
                  borderRadius: "5px",
                  fontFamily: "'Fira Sans',Helvetica,Arial,Lucida,sans-serif",
                  fontSize: "13px",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Modifiez celle-ci si vous souhaitez l'adapter à
                  l'environnement d'installation. Prévoyez au moins 10mm de jeu
                  pour une installation entre murs (pour le passage des têtes de
                  vis et pour éviter de rayer le cache lors de la pose).{" "}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Hauteur avant */}
          <Box
            sx={{
              position: "absolute",
              top: "42%",
              left: "-10%",
              transform: "translate(50%, -50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#2c8998",
              color: "white",
              borderRadius: "10px",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              height: "35px",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              width: "70px !important",
              borderColor: "#3397a6",
              borderRadius: "50px",
              borderWidth: 1,
              borderStyle: "solid",
              zIndex: selectedInfo === "Havv" ? 5 : 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                fontWeight: "900",
                fontFamily:
                  "'Montserrat', Helvetica, Arial, Lucida, sans-serif",
                fontSize: "13px",
              }}
            >
              {tempFrontHeight}
            </Typography>
            <InfoIcon
              sx={{
                fontSize: "15px",
                position: "absolute",
                top: "27%",
                right: "2%",
                color: "#ffffff",
                ml: 1,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleInfoClick("Havv");
              }}
            />
            {selectedInfo === "Havv" && (
              <Box
                ref={popupRef}
                sx={{
                  position: "absolute",
                  top: "35%",
                  right: isXs ? "-62vw" : "12%",
                  mt: 1,
                  width: isXs ? "75vw" : "400px",
                  zIndex: 6,
                  fontWeight: "300",
                  padding: "20px",
                  backgroundColor: "#f9f9f9",
                  boxShadow: "0 0 35px rgb(0 0 0 / 19%)",
                  borderRadius: "5px",
                  fontFamily: "'Fira Sans',Helvetica,Arial,Lucida,sans-serif",
                  fontSize: "13px",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  La pente de 2° sur le dessus du cache, qui permet le bon
                  écoulement de l'eau, implique une hauteur différente entre
                  avant et arrière. La hauteur à l'avant résulte de la hauteur à
                  l'arrière.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Profondeur (sizeZ) */}
          <Box
            sx={{
              position: "absolute",
              bottom: "13%",
              right: "10%",
              transform: "translateX(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "white",
              height: "35px",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              width: "70px !important",
              borderColor: "#3397a6",
              borderRadius: "50px",
              borderWidth: 1, // Ajoutez une largeur de bordure explicite
              borderStyle: "solid", // Définir un style de bordure
              zIndex: selectedInfo === "Prof" ? 5 : 1,
            }}
          >
            <TextField
              value={tempSizeZ} // Utilisation de la valeur temporaire
              onChange={handleChangeSizeZ} // Mise à jour de la valeur temporaire
              onBlur={handleBlurSizeZ}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleBlurSizeZ();
                  e.target.blur();
                }
              }}
              variant="outlined"
              size="small"
              sx={{
                width: "100%",
                textAlign: "center",
                "& input": {
                  textAlign: "center",
                  fontWeight: "900",
                  fontFamily:
                    "'Montserrat', Helvetica, Arial, Lucida, sans-serif",
                  fontSize: "13px",
                },
                "& fieldset": { border: "none" },
              }}
            />
            <InfoIcon
              sx={{
                color: "#acacac",
                fontSize: "15px",
                position: "absolute",
                top: "27%",
                right: "3%",
                ml: 1,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleInfoClick("Prof");
              }}
            />
            {selectedInfo === "Prof" && (
              <Box
                ref={popupRef}
                sx={{
                  position: "absolute",
                  top: "35%",
                  right: isXs ? "-13vw" : "12%",
                  mt: 1,
                  width: isXs ? "75vw" : "400px",
                  zIndex: 6,
                  fontWeight: "300",
                  padding: "20px",
                  backgroundColor: "#f9f9f9",
                  boxShadow: "0 0 35px rgb(0 0 0 / 19%)",
                  borderRadius: "5px",
                  fontFamily: "'Fira Sans',Helvetica,Arial,Lucida,sans-serif",
                  fontSize: "13px",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Modifiez celle-ci si vous souhaitez l'adapter à
                  l'environnement d'installation. Attention, sa réduction risque
                  de pénaliser le flux d'air.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Hauteur arrière (sizeY)*/}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              right: "10%",
              transform: "translate(50%, -50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "white",
              height: "35px",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              width: "70px !important",
              borderColor: "#3397a6",
              borderRadius: "50px",
              borderWidth: 1, // Ajoutez une largeur de bordure explicite
              borderStyle: "solid", // Définir un style de bordure
              zIndex: selectedInfo === "Harr" ? 5 : 1,
            }}
          >
            <TextField
              value={tempSizeY} // Utilisation de la valeur temporaire
              onChange={handleChangeSizeY} // Mise à jour de la valeur temporaire
              onBlur={handleBlurSizeY}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleBlurSizeY();
                  e.target.blur();
                }
              }}
              variant="outlined"
              size="small"
              sx={{
                width: "100%",
                textAlign: "center",
                "& input": {
                  textAlign: "center",
                  fontWeight: "900",
                  fontFamily:
                    "'Montserrat', Helvetica, Arial, Lucida, sans-serif",
                  fontSize: "13px",
                },
                "& fieldset": { border: "none" },
              }}
            />
            <InfoIcon
              sx={{
                fontSize: "15px",
                position: "absolute",
                top: "27%",
                right: "3%",
                ml: 1,
                color: "#acacac",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleInfoClick("Harr");
              }}
            />
            {selectedInfo === "Harr" && (
              <Box
                ref={popupRef}
                sx={{
                  position: "absolute",
                  top: "100%",
                  right: isXs ? "2.5vw" : "12%",
                  mt: 1,
                  width: isXs ? "80vw" : "400px",
                  zIndex: 6,
                  fontWeight: "300",
                  padding: "20px",
                  backgroundColor: "#f9f9f9",
                  boxShadow: "0 0 35px rgb(0 0 0 / 19%)",
                  borderRadius: "5px",
                  fontFamily: "'Fira Sans',Helvetica,Arial,Lucida,sans-serif",
                  fontSize: "13px",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  La pente de 2° sur le dessus du cache, qui permet le bon
                  écoulement de l'eau, implique une hauteur différente entre
                  avant et arrière. Modifiez celle-ci si vous devez insérer le
                  cache sous un rebord de fenêtre par exemple.{" "}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Erreur */}
          {dimensionsErrorString && (
            <Box
              sx={{
                display: "block",
                position: "absolute",
                top: "0",
                width: "100%",
                padding: "15px",
                zIndex: 60,
              }}
            >
              <InfoNotice
                content={dimensionsErrorString}
                padding="10px 20px"
                addContactUs={dimensionsNeedDevis}
                fontWeight="900"
                isError={isDimensionsError}
              />
            </Box>
          )}

          {/* Infobulles flottantes */}
          <IconButton
            sx={{
              position: "absolute",
              top: "22%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "#1976d2",
              zIndex: 4,
            }}
            onClick={() => setView("top")} // Changement de vue
          >
            <InfoIcon sx={{ fontSize: "18px", color: theme.palette.custom.blue }} />
          </IconButton>

          <IconButton
            sx={{
              position: "absolute",
              bottom: "49%",
              right: "25%",
              transform: "translate(50%, 50%)",
              color: "#1976d2",
              zIndex: 4,
            }}
            onClick={() => setView("side")} // Changement de vue
          >
            <InfoIcon sx={{ fontSize: "18px", color: theme.palette.custom.blue }} />
          </IconButton>

          <IconButton
            sx={{
              position: "absolute",
              top: "38%",
              right: "45%",
              transform: "translate(50%, -50%)",
              color: "#1976d2",
              zIndex: 4,
            }}
            onClick={() => setView("zoom")} // Changement de vue vers zoom
          >
            <InfoIcon sx={{ fontSize: "18px", color: theme.palette.custom.blue }} />
          </IconButton>
        </Box>
      </>
    );
  };

  const infos = {
    Harr: "La pente de 2° sur le dessus du cache, qui permet le bon écoulement de l'eau, implique une hauteur différente entre avant et arrière. Modifiez celle-ci si vous devez insérer le cache sous un rebord de fenêtre par exemple.",
    Largeur:
      "Modifiez celle-ci si vous souhaitez l'adapter à l'environnement d'installation. Prévoyez au moins 10mm de jeu pour une installation entre murs (pour le passage des têtes de vis et pour éviter de rayer le cache lors de la pose).",
    Prof: "Modifiez celle-ci si vous souhaitez l'adapter à l'environnement d'installation. Attention, sa réduction risque de pénaliser le flux d'air.",
    Havv: "",
  };

  const handleInfoClick = (infoId) => {
    setSelectedInfo(selectedInfo === infoId ? null : infoId);
  };

  const [selectedInfo, setSelectedInfo] = useState(null);
  const popupRef = useRef(null);

  const handleClickOutside = (event) => {
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      setSelectedInfo(null);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Box
      onClick={handleClick}
      sx={{
        position: "relative",
        textAlign: "center",
        mt: 4,
        width: "100%",
        maxWidth: "400px",
        mx: "auto",
        cursor: view === "default" ? "default" : "pointer",
      }}
    >
      {view === "default" && renderDefaultView()}
      {view === "side" && renderSideView()}
      {view === "top" && renderTopView()}
      {view === "zoom" && renderZoomView()}

      {isChanged && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            paddingY: 4,
            position: "relative",
            zIndex: 70,
          }}
        >
          {isChanged && (
            <Button
              title="Annuler mes modifications"
              variant="contained"
              onClick={handleResetClick}
              sx={{
                fontFamily: "'Fira Sans', Helvetica, Arial, Lucida, sans-serif",
                color: "#ffffff",
                backgroundColor: theme.palette.custom.blue,
                fontSize: "12px",
                borderRadius: 10,
                "&:hover": {
                  backgroundColor: "#005f96",
                },
              }}
            >
              Annuler mes modifications
            </Button>
          )}
        </Box>

      )}
    </Box>
  );
};

export default CacheDimensionsForm;
